<script setup lang="ts">
import {
  Trash2 as Trash2Icon,
  Eye as EyeIcon,
  EyeOff as EyeOffIcon,
  Download as DownloadIcon,
  SquarePlus as SquarePlusIcon,
  PenLine as PenLineIcon,
  MoreHorizontal as MoreHorizontalIcon,
  ChevronDown as ChevronDownIcon,
  ChevronRight as ChevronRightIcon,
  Plus as PlusIcon,
} from '@lucide/vue'
import { useEventListener } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, ref, watch, nextTick, type ComponentPublicInstance } from 'vue'

import InlineEditField from '@/components/ui/InlineEditField.vue'
import {
  openAddToCollection,
  waypointToAddToCollectionPoint,
} from '@/services/addToCollectionService'
import { showConfirm } from '@/services/dialogService'
import { downloadFile } from '@/services/downloadService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { openMobileRenameSheet } from '@/services/mobileRenameService'
import { requirePermission } from '@/services/permissionService'
import { showError } from '@/services/toastService'
import { isScreenSizeBelow, isSmallScreen, ScreenSize } from '@/services/uiService'
import { getWaypointIcon } from '@/services/waypointIconService'
import { useAuthStore } from '@/stores/authStore'
import { useLibraryStore } from '@/stores/libraryStore'
import { useUiStore } from '@/stores/uiStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import type { Tour, Waypoint } from '@/types/tour'
import { requireItemEditable } from '@/utils/permissionDialog'

const props = defineProps<{
  tour?: Tour
  expanded?: boolean
  showHeader?: boolean
  canEditWaypoints?: boolean
  canDeleteWaypoints?: boolean
}>()

const workspaceStore = useWorkspaceStore()
const libraryStore = useLibraryStore()
const uiStore = useUiStore()
const authStore = useAuthStore()
const { setHoveredWaypoint } = uiStore
const { hoveredWaypointId } = storeToRefs(uiStore)

const isLibraryContext = computed(() => !workspaceStore.clientId)

const emit = defineEmits<{
  'zoom-waypoint': [tourId: string, waypointIndex: number]
  'open-waypoint-detail': [tourId: string, waypointIndex: number, source?: 'list' | 'map']
  'update:expanded': [value: boolean]
}>()

const waypointListRef = ref<HTMLElement | null>(null)
const waypointElementRefs = ref<Map<string, HTMLElement>>(new Map())
const contextMenuWaypointId = ref<string | null>(null)
const contextMenuStyle = ref<Record<string, string>>({})
const renamingWaypointId = ref<string | null>(null)
const renameValue = ref('')
const renameInputRef = ref<{ inputEl: HTMLInputElement | null } | null>(null)

const isExpanded = ref(
  props.expanded !== undefined ? props.expanded : !isScreenSizeBelow(ScreenSize.MD),
)

watch(
  () => props.expanded,
  (newValue) => {
    if (newValue !== undefined) {
      isExpanded.value = newValue
    }
  },
  { immediate: true },
)

const findTour = (tourId: string): Tour | undefined => {
  if (isLibraryContext.value) {
    return libraryStore.detailTour?.clientId === tourId ? libraryStore.detailTour : undefined
  }
  return workspaceStore.tours.find((tour) => tour.clientId === tourId)
}

const waypoints = computed(() => {
  if (props.tour) {
    return props.tour.waypoints.map((waypoint, index) => ({
      waypoint,
      tourId: props.tour!.clientId,
      index,
    }))
  }

  const waypointsWithTourInfo: { waypoint: Waypoint; tourId: string; index: number }[] = []

  workspaceStore.tours.forEach((tour: Tour) => {
    if (!tour.active) return

    tour.waypoints.forEach((waypoint: Waypoint, index: number) => {
      waypointsWithTourInfo.push({
        waypoint,
        tourId: tour.clientId,
        index,
      })
    })
  })

  return waypointsWithTourInfo
})

const getWaypointId = (tourId: string, waypointIndex: number) => `${tourId}_${waypointIndex}`

const parseWaypointId = (waypointId: string) => {
  const separator = waypointId.lastIndexOf('_')
  if (separator === -1) return null
  const tourId = waypointId.slice(0, separator)
  const waypointIndex = Number(waypointId.slice(separator + 1))
  if (Number.isNaN(waypointIndex)) return null
  return { tourId, waypointIndex }
}

const findWaypointEntry = (tourId: string, waypointIndex: number) => {
  const tour = findTour(tourId)
  if (!tour || waypointIndex < 0 || waypointIndex >= tour.waypoints.length) return null
  const waypoint = tour.waypoints[waypointIndex]
  if (!waypoint) return null
  return { tour, waypoint }
}

const deleteAllWaypoints = async () => {
  if (!(await requirePermission('delete_waypoint'))) return
  const waypointCount = waypoints.value.length
  if (waypointCount === 0) return
  // Check creator-only content edit *before* the "are you sure" prompt, so a non-creator sees
  // only the explainer instead of a confirm they can't actually act on.
  if (
    props.tour &&
    !(await requireItemEditable(props.tour.canEdit, 'waypoint', props.tour.createdByName))
  )
    return

  const confirmed = await showConfirm({
    title: 'Delete All Waypoints',
    message: `Are you sure you want to delete all ${waypointCount} waypoint${waypointCount > 1 ? 's' : ''}? This action cannot be undone.`,
    confirmText: 'Delete All',
    cancelText: 'Cancel',
    variant: 'danger',
  })

  if (!confirmed) return

  if (props.tour) {
    const tourWaypoints = [...props.tour.waypoints]
    for (let i = tourWaypoints.length - 1; i >= 0; i--) {
      if (isLibraryContext.value) {
        await libraryStore.deleteWaypoint(props.tour.clientId, i)
      } else {
        await workspaceStore.deleteWaypoint(props.tour.clientId, i)
      }
    }
    return
  }

  workspaceStore.deleteAllWaypoints()
}

const deleteWaypoint = async (tourId: string, waypointIndex: number) => {
  if (!(await requirePermission('delete_waypoint'))) return
  const entry = findWaypointEntry(tourId, waypointIndex)
  if (!entry) return
  // Check creator-only content edit *before* the "are you sure" prompt, so a non-creator sees
  // only the explainer instead of a confirm they can't actually act on.
  if (!(await requireItemEditable(entry.tour.canEdit, 'waypoint', entry.tour.createdByName))) return

  const waypointName = entry.waypoint.name || `Waypoint ${waypointIndex + 1}`
  const confirmed = await showConfirm({
    title: 'Delete Waypoint',
    message: `Are you sure you want to delete "${waypointName}"? This action cannot be undone.`,
    confirmText: 'Delete',
    cancelText: 'Cancel',
    variant: 'danger',
  })

  if (!confirmed) return

  if (isLibraryContext.value) {
    await libraryStore.deleteWaypoint(tourId, waypointIndex)
  } else {
    await workspaceStore.deleteWaypoint(tourId, waypointIndex)
  }
}

const toggleWaypointVisibility = () => {
  if (props.tour) {
    if (!uiStore.areWaypointsVisible) {
      uiStore.setWaypointsVisible(true)
      workspaceStore.setTourWaypointVisibility(props.tour.clientId, true)
      return
    }

    workspaceStore.toggleTourWaypointVisibility(props.tour.clientId)
    return
  }

  uiStore.toggleWaypointVisibility()
}

const openWaypointDetail = (tourId: string, waypointIndex: number) => {
  emit('open-waypoint-detail', tourId, waypointIndex, 'list')
}

const handleAddWaypoint = () => {
  requirePermission('edit_waypoint').then((allowed) => {
    if (!allowed) return
    window.dispatchEvent(new CustomEvent('open-add-waypoint-modal'))
  })
}

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value
  emit('update:expanded', isExpanded.value)
}

const closeContextMenu = () => {
  contextMenuWaypointId.value = null
}

const updateFloatingPanelPosition = (
  event: MouseEvent,
  width: number,
  stylesRef: typeof contextMenuStyle,
) => {
  const viewportPadding = 8
  const left = Math.min(event.clientX, window.innerWidth - width - viewportPadding)
  const top = Math.min(event.clientY, window.innerHeight - 16 - viewportPadding)
  stylesRef.value = {
    position: 'fixed',
    left: `${Math.max(viewportPadding, left)}px`,
    top: `${Math.max(viewportPadding, top)}px`,
  }
}

const openWaypointContextMenu = (tourId: string, waypointIndex: number, event: MouseEvent) => {
  event.preventDefault()
  event.stopPropagation()
  contextMenuWaypointId.value = getWaypointId(tourId, waypointIndex)
  updateFloatingPanelPosition(event, 192, contextMenuStyle)
}

const startInlineRename = async (tourId: string, waypointIndex: number) => {
  if (props.canEditWaypoints === false) return
  if (!(await requirePermission('edit_waypoint'))) return
  const entry = findWaypointEntry(tourId, waypointIndex)
  if (!entry) return

  closeContextMenu()

  if (isSmallScreen.value) {
    openMobileRenameSheet({
      title: 'Rename waypoint',
      currentName: entry.waypoint.name || `Waypoint ${waypointIndex + 1}`,
      submit: async (name) => {
        if (isLibraryContext.value) {
          await libraryStore.updateWaypoint(tourId, waypointIndex, { name })
        } else {
          await workspaceStore.updateWaypoint(tourId, waypointIndex, { name })
        }
      },
    })
    return
  }

  renameValue.value = entry.waypoint.name || `Waypoint ${waypointIndex + 1}`
  renamingWaypointId.value = getWaypointId(tourId, waypointIndex)
  nextTick(() => {
    renameInputRef.value?.inputEl?.focus()
    renameInputRef.value?.inputEl?.select()
  })
}

const commitInlineRename = async (tourId: string, waypointIndex: number) => {
  const activeId = getWaypointId(tourId, waypointIndex)
  if (renamingWaypointId.value !== activeId) return
  const trimmed = renameValue.value.trim()
  renamingWaypointId.value = null
  if (!trimmed) return

  if (isLibraryContext.value) {
    await libraryStore.updateWaypoint(tourId, waypointIndex, { name: trimmed })
  } else {
    await workspaceStore.updateWaypoint(tourId, waypointIndex, { name: trimmed })
  }
}

const cancelInlineRename = () => {
  renamingWaypointId.value = null
  renameValue.value = ''
}

const escapeXml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')

const buildWaypointGpx = (waypoint: Waypoint) => {
  const parts = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<gpx version="1.1" creator="RouteCamp" xmlns="http://www.topografix.com/GPX/1/1">',
    `  <wpt lat="${waypoint.lat}" lon="${waypoint.lon}">`,
  ]

  if (typeof waypoint.ele === 'number') {
    parts.push(`    <ele>${waypoint.ele}</ele>`)
  }
  if (waypoint.name?.trim()) {
    parts.push(`    <name>${escapeXml(waypoint.name.trim())}</name>`)
  }
  if (waypoint.description?.trim()) {
    parts.push(`    <desc>${escapeXml(waypoint.description.trim())}</desc>`)
  }
  if (waypoint.time) {
    parts.push(`    <time>${new Date(waypoint.time).toISOString()}</time>`)
  }
  if (waypoint.type?.trim()) {
    parts.push(`    <sym>${escapeXml(waypoint.type.trim())}</sym>`)
  }

  parts.push('  </wpt>', '</gpx>')
  return parts.join('\n')
}

const downloadWaypoint = async (tourId: string, waypointIndex: number) => {
  const entry = findWaypointEntry(tourId, waypointIndex)
  if (!entry) return
  closeContextMenu()

  const gpx = buildWaypointGpx(entry.waypoint)
  const blob = new Blob([gpx], { type: 'application/gpx+xml' })
  const fileName = `${(entry.waypoint.name?.trim() || 'waypoint').replace(/[\\/:*?"<>|]+/g, '_')}.gpx`

  try {
    await downloadFile(blob, fileName, { mimeType: 'application/gpx+xml' })
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to download waypoint GPX', { error })
    showError(
      `Failed to download waypoint: ${error instanceof Error ? error.message : String(error)}`,
    )
  }
}

const openAddToCollectionPicker = (tourId: string, waypointIndex: number, event: MouseEvent) => {
  if (!authStore.isAuthenticated) {
    window.dispatchEvent(new CustomEvent('open-auth-modal', { detail: { view: 'login' } }))
    return
  }
  event.stopPropagation()
  const entry = findWaypointEntry(tourId, waypointIndex)
  if (!entry) return
  closeContextMenu()
  openAddToCollection({
    points: [waypointToAddToCollectionPoint(entry.waypoint)],
    sourceWorkspaceId: workspaceStore.clientId,
  })
}

const handleContextMenuRename = async () => {
  if (!contextMenuWaypointId.value) return
  const parsed = parseWaypointId(contextMenuWaypointId.value)
  if (!parsed) return
  await startInlineRename(parsed.tourId, parsed.waypointIndex)
}

const handleContextMenuDownload = async () => {
  if (!contextMenuWaypointId.value) return
  const parsed = parseWaypointId(contextMenuWaypointId.value)
  if (!parsed) return
  await downloadWaypoint(parsed.tourId, parsed.waypointIndex)
}

const handleContextMenuCopy = async (event: MouseEvent) => {
  if (!contextMenuWaypointId.value) return
  const parsed = parseWaypointId(contextMenuWaypointId.value)
  if (!parsed) return
  openAddToCollectionPicker(parsed.tourId, parsed.waypointIndex, event)
}

const handleContextMenuDelete = async () => {
  if (!contextMenuWaypointId.value) return
  const parsed = parseWaypointId(contextMenuWaypointId.value)
  if (!parsed) return
  await deleteWaypoint(parsed.tourId, parsed.waypointIndex)
  closeContextMenu()
}

const setWaypointElementRef = (
  el: Element | ComponentPublicInstance | null,
  waypointId: string,
) => {
  if (el instanceof HTMLElement) {
    waypointElementRefs.value.set(waypointId, el)
  } else {
    waypointElementRefs.value.delete(waypointId)
  }
}

const handleWaypointRowClick = (tourId: string, waypointIndex: number) => {
  if (props.tour) {
    openWaypointDetail(tourId, waypointIndex)
    emit('zoom-waypoint', tourId, waypointIndex)
    return
  }

  emit('zoom-waypoint', tourId, waypointIndex)
}

watch(
  () => hoveredWaypointId.value,
  (newId) => {
    if (newId && waypointListRef.value && isExpanded.value) {
      nextTick(() => {
        const hoveredElement = waypointElementRefs.value.get(newId)
        if (hoveredElement) {
          hoveredElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
      })
    }
  },
)

const effectiveWaypointVisibility = computed(() => {
  if (!props.tour) return uiStore.areWaypointsVisible

  const tourWaypointsVisible = props.tour._waypointsVisible ?? true
  return props.tour.visible !== false && tourWaypointsVisible && uiStore.areWaypointsVisible
})

const isWaypointVisible = (tourId: string, waypointIndex: number) =>
  !uiStore.isWaypointHidden(getWaypointId(tourId, waypointIndex))

const toggleSingleWaypointVisibility = (tourId: string, waypointIndex: number, event: Event) => {
  event.stopPropagation()
  uiStore.toggleSingleWaypointVisibility(getWaypointId(tourId, waypointIndex))
}

useEventListener(document, 'pointerdown', (event: Event) => {
  const target = event.target as HTMLElement
  if (!target.closest('[data-waypoint-context-menu]')) {
    closeContextMenu()
  }
})
</script>

<template>
  <div class="flex min-h-0 flex-col">
    <div
      v-if="props.showHeader !== false"
      class="flex justify-between items-center px-4 pt-3 pb-1 group"
    >
      <button
        @click="toggleExpanded"
        class="flex items-center flex-1 hover:text-primary-600 focus:outline-none hover:cursor-pointer"
      >
        <ChevronDownIcon v-if="isExpanded" class="w-3 h-3 text-gray-500" />
        <ChevronRightIcon v-else class="w-3 h-3 text-gray-500" />
        <span class="text-sm font-semibold text-gray-700 ml-1">
          Waypoints ({{ waypoints.length }})
        </span>
      </button>
      <div class="flex items-center gap-1">
        <button
          v-if="props.canEditWaypoints !== false"
          @click="handleAddWaypoint"
          class="p-0.5 text-gray-400 hover:text-primary-600 hover:cursor-pointer"
          title="Add waypoint"
        >
          <PlusIcon class="w-3 h-3" />
        </button>
        <button
          @click="toggleWaypointVisibility"
          class="flex items-center justify-center w-5 h-5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 hover:cursor-pointer rounded transition-colors shrink-0"
          :title="effectiveWaypointVisibility ? 'Hide all waypoints' : 'Show all waypoints'"
          :aria-label="effectiveWaypointVisibility ? 'Hide all waypoints' : 'Show all waypoints'"
        >
          <component :is="effectiveWaypointVisibility ? EyeIcon : EyeOffIcon" class="w-3.5 h-3.5" />
        </button>
        <button
          v-if="props.canDeleteWaypoints !== false"
          @click="deleteAllWaypoints"
          class="p-0.5 text-gray-400 hover:text-red-600 hover:cursor-pointer"
          title="Delete all waypoints"
        >
          <Trash2Icon class="w-3 h-3" />
        </button>
      </div>
    </div>

    <div v-if="isExpanded" class="flex-1 px-4 pb-2" :class="{ 'pt-3': props.showHeader === false }">
      <div ref="waypointListRef">
        <div
          v-for="(item, i) in waypoints"
          :key="`${item.tourId}-${item.index}`"
          :class="[
            'flex items-start py-1.5 -mx-1 px-1 rounded transition-colors duration-200 hover:cursor-pointer group/wp',
            hoveredWaypointId === `${item.tourId}_${item.index}`
              ? 'bg-primary-50'
              : 'hover:bg-gray-50',
          ]"
          :data-tour-id="item.tourId"
          :data-index="item.index"
          :data-waypoint-id="`${item.tourId}_${item.index}`"
          :ref="(el) => setWaypointElementRef(el, `${item.tourId}_${item.index}`)"
          @mouseenter="setHoveredWaypoint(`${item.tourId}_${item.index}`)"
          @click="handleWaypointRowClick(item.tourId, item.index)"
          @contextmenu="openWaypointContextMenu(item.tourId, item.index, $event)"
          @mouseleave="setHoveredWaypoint(null)"
        >
          <div class="text-sm mr-1.5 mt-px shrink-0">
            <component
              :is="getWaypointIcon(item.waypoint.type)"
              :size="14"
              stroke="currentColor"
              :stroke-width="2"
              :absolute-stroke-width="true"
              class="text-gray-500"
            />
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center justify-between">
              <InlineEditField
                v-if="renamingWaypointId === `${item.tourId}_${item.index}`"
                ref="renameInputRef"
                v-model="renameValue"
                @commit="commitInlineRename(item.tourId, item.index)"
                @cancel="cancelInlineRename"
              />
              <span
                v-else
                class="text-sm text-gray-700 truncate"
                :title="item.waypoint.name || 'Waypoint'"
                :class="{ '!text-gray-400': !isWaypointVisible(item.tourId, item.index) }"
              >
                {{ item.waypoint.name || `Waypoint ${i + 1}` }}
              </span>
              <div
                v-if="renamingWaypointId !== `${item.tourId}_${item.index}`"
                class="ml-2 flex shrink-0 items-center gap-0.5"
              >
                <!-- Overflow menu (Rename / Download / Copy / Delete) — always visible so the actions
                     are reachable with one tap on mobile, not just via right-click/long-press. -->
                <button
                  @click.stop="openWaypointContextMenu(item.tourId, item.index, $event)"
                  class="flex h-5 w-5 items-center justify-center rounded text-gray-500 transition-colors hover:cursor-pointer hover:bg-gray-100 hover:text-gray-700"
                  title="More actions"
                  aria-label="Waypoint actions"
                >
                  <MoreHorizontalIcon class="h-3.5 w-3.5" />
                </button>
                <button
                  @click="toggleSingleWaypointVisibility(item.tourId, item.index, $event)"
                  class="flex h-5 w-5 items-center justify-center rounded transition-colors hover:cursor-pointer"
                  :class="
                    isWaypointVisible(item.tourId, item.index)
                      ? 'text-gray-500 hover:text-gray-700 opacity-0 group-hover/wp:opacity-100'
                      : 'text-gray-500 hover:text-gray-700 opacity-100'
                  "
                  :title="
                    isWaypointVisible(item.tourId, item.index) ? 'Hide on map' : 'Show on map'
                  "
                  :aria-label="
                    isWaypointVisible(item.tourId, item.index) ? 'Hide on map' : 'Show on map'
                  "
                >
                  <component
                    :is="isWaypointVisible(item.tourId, item.index) ? EyeIcon : EyeOffIcon"
                    class="h-3.5 w-3.5"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="waypoints.length === 0 && !isScreenSizeBelow(ScreenSize.MD)"
          class="py-3 text-center text-xs text-gray-400"
        >
          <p>No waypoints available.</p>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="contextMenuWaypointId"
        data-waypoint-context-menu
        :style="contextMenuStyle"
        class="fixed z-[9999] w-48 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden py-1"
      >
        <button
          v-if="props.canEditWaypoints !== false"
          @click.stop="handleContextMenuRename"
          class="w-full px-3 py-2 text-xs text-left text-gray-700 hover:bg-gray-50 hover:cursor-pointer flex items-center gap-2"
        >
          <PenLineIcon class="w-3.5 h-3.5 text-gray-500 shrink-0" />
          Rename
        </button>
        <button
          @click.stop="handleContextMenuDownload"
          class="w-full px-3 py-2 text-xs text-left text-gray-700 hover:bg-gray-50 hover:cursor-pointer flex items-center gap-2"
        >
          <DownloadIcon class="w-3.5 h-3.5 text-gray-500 shrink-0" />
          Download
        </button>
        <button
          @click.stop="handleContextMenuCopy"
          class="w-full px-3 py-2 text-xs text-left text-gray-700 hover:bg-gray-50 hover:cursor-pointer flex items-center gap-2"
        >
          <SquarePlusIcon class="w-3.5 h-3.5 text-gray-500 shrink-0" />
          Add to collection
        </button>
        <hr class="my-1 border-gray-100" />
        <button
          v-if="props.canDeleteWaypoints !== false"
          @click.stop="handleContextMenuDelete"
          class="w-full px-3 py-2 text-xs text-left text-red-600 hover:bg-red-50 hover:cursor-pointer flex items-center gap-2"
        >
          <Trash2Icon class="w-3.5 h-3.5 shrink-0" />
          Delete
        </button>
      </div>
    </Teleport>
  </div>
</template>
